import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { CatchPriceListRequest, CatchPriceListResponse, CatchPriceListsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { Equal, handleError, showNotFoundErrorNotification, showWarningNotification } from 'src/utility/utils';
import { CatchPriceListDataModel } from 'src/views/contract/vesselContract/Import/DataTypes';

// Other Redux
import { getAllProducts, selectProduct } from '@store/masterData/productSlice';
import { getAllProductSpecies, ProductSpecies, selectProductSpecies } from '@store/masterData/productSpeciesSlice';
import { getAllProductQuality, ProductQuality, selectProductQuality } from '@store/masterData/productQualitySlice';
import { getAllProductSize, ProductSize, selectProductSize } from '@store/masterData/productSizeSlice';
import { getAllProductState, ProductState, selectProductState } from '@store/masterData/productStateSlice';

export interface CatchPriceList extends CatchPriceListResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  contractCatchPriceList: CatchPriceList[];
}

const initialState: State = {
  isLoading: true,
  error: false,
  contractCatchPriceList: [],
};

const contractCatchPriceListSlice = createSlice({
  name: 'contractCatchPriceList',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getContractCatchPriceListSuccess: (state, action: PayloadAction<CatchPriceList[]>) => {
      state.contractCatchPriceList = action.payload;
      state.isLoading = false;
    },
    addNewCatchPriceListSuccess: (state, action: PayloadAction<CatchPriceList>) => {
      const newItem = action.payload;
      state.contractCatchPriceList = [newItem, ...state.contractCatchPriceList];
      toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
      state.isLoading = false;
    },
    updateCatchPriceListSuccess: (state, action: PayloadAction<CatchPriceList>) => {
      const updatedItem = action.payload;
      const index = state.contractCatchPriceList.findIndex(item => item.catchPriceListId === updatedItem.catchPriceListId);
      state.contractCatchPriceList = [...state.contractCatchPriceList.slice(0, index), updatedItem, ...state.contractCatchPriceList.slice(index + 1)];
      toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
      state.isLoading = false;
    },
    deleteCatchPriceListSuccess: (state, action: PayloadAction<number>) => {
      const catchPriceListId = action.payload;
      state.contractCatchPriceList = state.contractCatchPriceList.filter(item => item.catchPriceListId !== catchPriceListId);
      state.isLoading = false;
    },
  },
});

// Reducer
export default contractCatchPriceListSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
} = contractCatchPriceListSlice.actions;

// for useSelector
export const selectContractCatchPriceList = (state: RootState) => state.contractCatchPriceList as State;

// ----------------------------------------------------------------------

export function getContractCatchPriceList(contractNumber: string) {
  return async (dispatch, getState) => {
    dispatch(contractCatchPriceListSlice.actions.startLoading());
    try {
      const client = new CatchPriceListsClient(undefined, axios);
      const response = await client.getByVesselContractNumber(contractNumber);
      dispatch(contractCatchPriceListSlice.actions.getContractCatchPriceListSuccess(response));
      return response;
    } catch (error) {
      dispatch(contractCatchPriceListSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createCatchPriceList(catchPriceList: CatchPriceList) {
  return async (dispatch, getState) => {
    dispatch(contractCatchPriceListSlice.actions.startLoading());
    try {
      const { catchPriceListId, ...payload } = catchPriceList;
      const request = payload as CatchPriceListRequest;
      const client = new CatchPriceListsClient(undefined, axios);
      const response = await client.create(request);
      dispatch(contractCatchPriceListSlice.actions.addNewCatchPriceListSuccess(response));
    } catch (error) {
      dispatch(contractCatchPriceListSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createCatchPriceListBatch(vesselContractId: number,
  catchPriceListData: CatchPriceListDataModel[],
  toastId: ReactText, currentProgress: number, totalProgress: number) {
  return async (dispatch, getState) => {
    try {
      await dispatch(getAllProducts());
      await dispatch(getAllProductSpecies());
      await dispatch(getAllProductQuality());
      await dispatch(getAllProductSize());
      await dispatch(getAllProductState());

      const { productList } = selectProduct(getState());
      const { productSpeciesList } = selectProductSpecies(getState());
      const { productQualityList } = selectProductQuality(getState());
      const { productSizeList } = selectProductSize(getState());
      const { productStateList } = selectProductState(getState());

      const client = new CatchPriceListsClient(undefined, axios);
      const notFoundItems: string[] = [];
      for (const d of catchPriceListData) {
        const productId = productList.find(p =>
          Equal(p.productNumber, d.ProductNumber) ||
          Equal(p.oldProductNumber, d.ProductNumber))?.productId;
        if (!productId) {
          notFoundItems.push(d.ProductNumber ?? 'null');
          continue;
        }

        const productSpeciesId = productSpeciesList.find((p: ProductSpecies) =>
          Equal(p.productSpeciesCode, d.ProductSpecies))?.productSpeciesId;
        if (!productSpeciesId) {
          notFoundItems.push(d.ProductSpecies ?? 'null');
          continue;
        }

        const productQualityId = productQualityList.find((p: ProductQuality) =>
          Equal(p.productQualityCode, d.ProductQuality))?.productQualityId;
        if (!productQualityId) {
          notFoundItems.push(d.ProductQuality ?? 'null');
          continue;
        }

        const productSizeId = productSizeList.find((p: ProductSize) =>
          Equal(p.productSizeCode, d.ProductSize))?.productSizeId;
        if (!productSizeId) {
          notFoundItems.push(d.ProductSize ?? 'null');
          continue;
        }

        const productStateId = productStateList.find((p: ProductState) =>
          Equal(p.productStateCode, d.ProductState))?.productStateId;
        if (!productStateId) {
          notFoundItems.push(d.ProductState ?? 'null');
          continue;
        }

        const request = {
          vesselContractId,
          productId,
          productSpeciesId,
          productQualityId,
          productSizeId,
          productStateId,
          ratePerKg: d.RatePerKg
        } as CatchPriceListRequest;
        await client.create(request);
        currentProgress += 1;
        toast.update(toastId, { render: `${stringResources.IMPORT.CATCH_PRICE_LIST_IMPORTING} (${currentProgress}/${totalProgress})`, progress: currentProgress / totalProgress });
        if (notFoundItems.length > 0) {
          showNotFoundErrorNotification(notFoundItems.join(','));
        }
      }
    } catch (error) {
      handleError(error);
    }
  };
}

export function updateCatchPriceList(catchPriceList: CatchPriceList) {
  return async (dispatch, getState) => {
    dispatch(contractCatchPriceListSlice.actions.startLoading());
    try {
      const { catchPriceListId, ...payload } = catchPriceList;
      const request = payload as CatchPriceListRequest;
      const client = new CatchPriceListsClient(undefined, axios);
      const response = await client.edit(catchPriceListId, request);
      dispatch(contractCatchPriceListSlice.actions.updateCatchPriceListSuccess(response));
    } catch (error) {
      dispatch(contractCatchPriceListSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function deleteCatchPriceList(catchPriceListId: number, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(contractCatchPriceListSlice.actions.startLoading());
    try {
      const client = new CatchPriceListsClient(undefined, axios);
      await client.delete(catchPriceListId);
      dispatch(contractCatchPriceListSlice.actions.deleteCatchPriceListSuccess(catchPriceListId));
      if (showNotification) {
        toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
      }
      return true;
    } catch (error) {
      dispatch(contractCatchPriceListSlice.actions.hasError(true));
      handleError(error);
      return false;
    }
  };
}

export async function getDefaultRatePerKg(vesselContractNumber: string,
  productNumber: string,
  productSpeciesCode: string,
  productQualityCode: string,
  productStateCode: string,
  productSizeCode: string,
  showWarning: boolean) {
  try {
    const client = new CatchPriceListsClient(undefined, axios);
    const res = await client.findPrice(vesselContractNumber, productNumber, productSpeciesCode, productQualityCode, productStateCode, productSizeCode);
    if (!res && showWarning) {
      showWarningNotification(stringResources.REDUX_SLICE.NO_DEFAULT_RATE);
    }
    return res;
  } catch (error: any) {
    handleError(error);
  }
}

export function findContractRatePerKg(
  contractCatchPriceList: CatchPriceList[],
  productId: number | null,
  productSpeciesId: number | null,
  productQualityId: number | null,
  productSizeId: number | null,
  productStateId: number | null,
  showWarning: boolean): number | null {

  if (!productId || !productSpeciesId || !productQualityId || !productSizeId || !productStateId) return null;

  const matchedCatchPrice = contractCatchPriceList.find(x =>
    x.productId === productId &&
    x.productSpeciesId === productSpeciesId &&
    x.productQualityId === productQualityId &&
    x.productSizeId === productSizeId &&
    x.productStateId === productStateId
  );
  if (!matchedCatchPrice && showWarning) {
    showWarningNotification(stringResources.REDUX_SLICE.NO_DEFAULT_RATE);
  }
  return matchedCatchPrice?.ratePerKg ?? null;
}